import type { SonioxError } from '@/lib/soniox/errors';
import { convexQuery } from '@convex-dev/react-query';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';
import { useAudioCapture } from './use-audio-capture';
import { useLiveTranscription } from './use-live-transcription';
import {
  isActive,
  useRecordingSession,
  type TranscriptionStatus,
} from './use-recording-session';

export { isActive };
export type { TranscriptionStatus };

const AUTOSAVE_INTERVAL_MS = 500;

/**
 * Coordinator hook that composes session lifecycle, audio capture,
 * and live transcription into a single recording flow.
 */
export const useTranscription = ({ reportId }: { reportId: Id<'reports'> }) => {
  const session = useRecordingSession({ reportId });
  const capture = useAudioCapture({ reportId });
  const transcription = useLiveTranscription({ reportId });

  const { data: report } = useQuery(
    convexQuery(api.reports.get, reportId ? { id: reportId } : 'skip'),
  );

  // Autosave during active transcription
  useEffect(() => {
    if (session.status !== 'transcribing') return;

    const intervalId = setInterval(() => {
      void transcription.saveText();
    }, AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [session.status, transcription.saveText]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      session.invalidateEpoch();
      transcription.cleanup();
      capture.cleanup();
      session.cleanupSegment();
    };
  }, [
    capture.cleanup,
    session.cleanupSegment,
    session.invalidateEpoch,
    transcription.cleanup,
  ]);

  const startTranscription = useCallback(async () => {
    if (session.statusRef.current !== 'idle') {
      console.warn(
        `Cannot start transcription while status is ${session.statusRef.current}.`,
      );
      return;
    }

    if (session.stopInFlightRef.current) {
      console.warn('Cannot start transcription while stop is in progress.');
      return;
    }

    const epoch = session.beginEpoch();

    // Pre-populate text state before switching display mode so
    // the live text view never renders empty strings.
    const currentDbText = report?.rawTranscription ?? '';
    const freshestKnownText =
      transcription.finalText.length > currentDbText.length
        ? transcription.finalText
        : currentDbText;

    transcription.init(freshestKnownText, 0);

    session.transitionStatus('connecting');

    try {
      const { seq, baseOffsetMs } = await session.startSegment();

      if (!session.isEpochCurrent(epoch)) {
        await session.cancelSegment();
        return;
      }

      capture.init(baseOffsetMs, seq);
      transcription.setBaseOffset(baseOffsetMs);

      const client = transcription.createClient({
        onAudioPayload: (payload) => {
          if (!session.isEpochCurrent(epoch)) return;
          capture.addPayload(payload);
        },
        onPartialResult: () => {
          // Text state is handled inside useLiveTranscription
        },
        onError: (sonioxError: SonioxError) => {
          if (!session.isEpochCurrent(epoch)) return;

          session.invalidateEpoch();
          console.error('Transcription error:', sonioxError);
          transcription.setError(sonioxError);
          session.transitionStatus('idle', { force: true });
          transcription.cleanup();
          capture.cleanup();

          void session.failSegment({
            code: sonioxError.status,
            message: sonioxError.message,
          });
        },
      });

      if (!session.isEpochCurrent(epoch)) {
        client.stop();
        return;
      }

      await transcription.startClient();

      if (!session.isEpochCurrent(epoch)) {
        transcription.cleanup();
        return;
      }

      session.transitionStatus('transcribing');
    } catch (startError) {
      if (!session.isEpochCurrent(epoch)) return;

      session.invalidateEpoch();
      console.error('Transcription start failed', startError);
      const message =
        startError instanceof Error ? startError.message : 'Failed to start';
      const transcriptionError: SonioxError = {
        status: 'media_recorder_error',
        message,
        code: undefined,
      };
      transcription.setError(transcriptionError);
      session.transitionStatus('idle', { force: true });
      transcription.cleanup();
      capture.cleanup();

      await session.failSegment({
        code: transcriptionError.status,
        message,
      });
    }
  }, [session, capture, transcription, report?.rawTranscription]);

  const stopTranscription = useCallback(async () => {
    if (session.stopInFlightRef.current) {
      await session.stopInFlightRef.current;
      return;
    }

    if (!isActive(session.statusRef.current)) {
      // Idempotent no-op: callers can safely request stop while already idle.
      return;
    }

    session.invalidateEpoch();
    session.transitionStatus('stopping');

    const stopPromise = (async () => {
      let segmentCommitted = false;
      try {
        await transcription.stopClient();
        await capture.flush();

        const hasAudio = capture.hasCapturedAudio();

        if (!hasAudio) {
          await session.cancelSegment();
          transcription.consolidateText();
          await transcription.saveText(true);
          capture.cleanup();
          return;
        }

        session.transitionStatus('finalizing');
        await session.beginFinalizing();
        await capture.commitSegment(transcription.consumeFinalTokensForRange);
        segmentCommitted = true;
        transcription.consolidateText();
        await transcription.saveText(true);
        session.completeSegment();
        capture.cleanup();
      } catch (stopError) {
        console.error('Stop transcription failed', stopError);
        if (segmentCommitted) {
          // Segment is already committed and report is ready; avoid overriding with failed status.
          session.completeSegment();
          capture.cleanup();
          return;
        }
        const message =
          stopError instanceof Error ? stopError.message : 'Failed to stop';
        await session.failSegment({
          code: 'stop_failed',
          message,
        });
        capture.cleanup();
      } finally {
        session.transitionStatus('idle', { force: true });
      }
    })();

    session.stopInFlightRef.current = stopPromise;

    try {
      await stopPromise;
    } finally {
      if (session.stopInFlightRef.current === stopPromise) {
        session.stopInFlightRef.current = null;
      }
    }
  }, [session, capture, transcription]);

  return {
    status: session.status,
    error: transcription.error,
    previousText: transcription.previousText,
    currentSegmentText: transcription.currentSegmentText,
    finalText: transcription.finalText,
    nonFinalText: transcription.nonFinalText,
    startTranscription,
    stopTranscription,
  };
};
